import React, { useCallback, useEffect, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import Button from '../Button/Button';
import ThirdPane from '../ThirdPane';
import WaterServiceDetail, {
  WaterService,
  WaterServiceStatus,
} from './WaterServiceDetail';
import WaterServiceEditModal from './WaterServiceEditModal';
import { PortalWithState } from 'react-portal';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import { Flex } from 'rebass';
import WaterServiceHistory from './WaterServiceHistory';
import useNotify from '../../hooks/useNotify';
import { gql } from '@/modes/waterSupplier/gql';
import RequirePerms from '@/components/RequirePerms';

export const GET_WATER_SERVICE = gql(`
  query GetWaterService($where: JSON) {
    waterServices(where: $where) {
      id
      waterSupplierId
      siteNumber
      meterNumber
      meterSize
      radioId
      pin
      recycledWaterSite {
        id
        name
      }
      type
      sourceType
      status
      tags
      addressId
      address {
        id
        street1
        street2
        city
        state
        zip
        zipExt
      }
      backflowAssemblies {
        id
        model {
          name
          backflowAssemblyMake {
            name
          }
        }
        size
        serialNumber
        testAnniversary
        lastTestDate
        lastTest {
          jobId
          initialResult
          finalResult
        }
        lastNoticeSent {
          created
        }
        status
        testStatus
        location
        type
        hazardType
        hazardLevel
      }
      serviceAddresses(where: { waterServiceId: { ne: null } }, limit: null) {
        id
        customerId
        customerRole
        active
        customer {
          status
          profile {
            name
          }
          accountNumber
        }
        sendNotices
      }
      comments {
        id
        profile {
          id
          name
        }
        comment
        created
      }
    }
  }
`);

const SET_WATER_SERVICE_STATUS = gql(`
  mutation SetWaterServiceStatus(
    $waterServiceId: ID!
    $status: WaterServiceStatus!
  ) {
    setWaterServiceStatus(waterServiceId: $waterServiceId, status: $status) {
      id
      status
    }
  }
`);

type WaterServiceThirdPaneProps = {
  waterServiceId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

export default function WaterServiceThirdPane({
  waterServiceId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: WaterServiceThirdPaneProps): JSX.Element {
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    waterServices: WaterService[];
  }>(GET_WATER_SERVICE, {
    skip: !waterServiceId,
    variables: { where: { id: waterServiceId || null } },
  });

  const [waterService, setWaterService] = useState<WaterService>();

  useEffect(() => {
    if (itemData && itemData?.waterServices?.length) {
      setWaterService(itemData?.waterServices[0]);
    }
  }, [waterServiceId, itemData, setWaterService]);

  if (itemError) {
    throw itemError;
  }

  const { showError, showSuccess, showWarning } = useNotify();

  const [setWaterServiceStatus] = useMutation(SET_WATER_SERVICE_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting water service status', error);
    }, []),
  });

  const isActive = waterService?.status === WaterServiceStatus.ACTIVE;

  const activateDeactivate = useCallback(() => {
    if (!waterService) {
      return;
    }

    const newStatus = isActive
      ? WaterServiceStatus.INACTIVE
      : WaterServiceStatus.ACTIVE;

    const actionMessage =
      newStatus === WaterServiceStatus.INACTIVE
        ? 'Are you sure you want to deactivate this service?'
        : 'Are you sure you want to reactivate this service?';

    if (!confirm(actionMessage)) {
      return;
    }

    setWaterServiceStatus({
      variables: { waterServiceId: waterService.id, status: newStatus },
      onCompleted: () => {
        if (newStatus === WaterServiceStatus.INACTIVE) {
          showWarning('Service deactivated');
        } else {
          showSuccess('Service activated');
        }
      },
    });
  }, [
    waterService,
    isActive,
    setWaterServiceStatus,
    showWarning,
    showSuccess,
    showError,
  ]);

  return (
    <ThirdPane visible={!!waterServiceId}>
      {waterService && (
        <ThirdPaneHeader
          onToggle={onToggle}
          title={waterService ? waterService?.siteNumber : 'Loading...'}
          onPrev={onPrev}
          onNext={onNext}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
        >
          <RequirePerms waterServices="UPDATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    mr={10}
                    icon="edit"
                    variant="outline"
                    onClick={openPortal}
                  >
                    Edit
                  </Button>
                  {portal(
                    <WaterServiceEditModal
                      onClose={closePortal}
                      waterService={waterService}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
          <RequirePerms waterServices="UPDATE">
            <Button
              mr={10}
              variant={isActive ? 'danger' : 'success'}
              onClick={activateDeactivate}
            >
              {isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          </RequirePerms>
        </ThirdPaneHeader>
      )}
      <ThirdPaneContent>
        {waterService && (
          <Flex height="100%" flexDirection="row" flexGrow={1} flexShrink={1}>
            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width="66%"
              overflowY="scroll"
            >
              <WaterServiceDetail
                waterService={waterService}
                onChange={refetch}
              />
            </Flex>
            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width="34%"
              sx={{ borderLeft: '1px solid', borderLeftColor: 'tableBorder' }}
            >
              {waterServiceId && waterService.comments && (
                <WaterServiceHistory
                  comments={waterService.comments}
                  refetch={refetch}
                  waterServiceId={waterServiceId}
                />
              )}
            </Flex>
          </Flex>
        )}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
